import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/Common";
import { ShieldCheck, Loader2, Lock, CreditCard } from "lucide-react";

/**
 * Faz 2D (v2) — İlk Ödeme: PayTR Direkt API Native HTML Form POST
 *
 * Doğru akış (PayTR resmi Direkt API):
 *  1. Bileşen mount olunca `POST /api/paytr/direct-charge {plan}` çağırır;
 *     backend `action` (https://www.paytr.com/odeme) + kart-DIŞI form
 *     `fields` (merchant_id, paytr_token, user_ip, merchant_oid, ...) döner.
 *  2. Kullanıcı kart bilgilerini bu bileşenin HTML formuna girer.
 *  3. Submit'te form NATIVE olarak doğrudan `action` URL'ine POST edilir
 *     (fetch/AJAX değil — browser navigation).  Kart bilgisi PENZ BACKEND'e
 *     ASLA gönderilmez.
 *  4. PayTR HTML yanıt döner (3DS challenge / success / fail sayfası);
 *     browser bu HTML'i render eder.
 *  5. Ödeme sonucu YALNIZCA `/api/paytr/callback` (server-to-server) üzerinden
 *     doğrulanır; subscription aktivasyonu callback'te yapılır.  merchant_ok_url
 *     yalnızca kullanıcı yönlendirmesidir.
 */
export default function DirectPaymentForm({ plan, onBack, renewalToken }) {
  const [session, setSession] = useState(null); // { action, fields, merchant_oid, total_amount_tl }
  const [loadErr, setLoadErr] = useState("");
  const [loading, setLoading] = useState(true);
  const formRef = useRef(null);
  const [ccOwner, setCcOwner] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryMonth, setExpiryMonth] = useState("");
  const [expiryYear, setExpiryYear] = useState("");
  const [cvv, setCvv] = useState("");
  const [failMsg, setFailMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // tr-TR görsel para formatı — 480.00 → "480,00 TL", 4800.00 → "4.800,00 TL"
  // NOT: bu formatlama YALNIZCA UI içindir; PayTR'a giden `payment_amount`
  // hidden input'u backend'in ürettiği "480.00" / "4800.00" formatını korur.
  const formatTL = (value) => {
    const n = Number(value);
    if (!isFinite(n)) return `${value ?? ""} TL`;
    return `${n.toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} TL`;
  };
  const displayTotal = session ? formatTL(session.total_amount_tl) : "";

  const cardMasked = useMemo(() => {
    const digits = (cardNumber || "").replace(/\D/g, "");
    return digits.replace(/(.{4})/g, "$1 ").trim();
  }, [cardNumber]);

  const planLabel = plan === "yearly" ? "Yıllık" : "Aylık";
  const renewalHint = plan === "yearly"
    ? "1 yıl sonra otomatik yenilenir"
    : "Her ay otomatik yenilenir";

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        const { data } = await api.post("/paytr/direct-charge", renewalToken ? { plan, renewal_token: renewalToken } : { plan });
        if (ok) {
          setSession(data);
          try { sessionStorage.setItem("penz_last_paytr_oid", data.merchant_oid || ""); } catch {}
        }
      } catch (err) {
        const msg = err?.response?.data?.detail;
        setLoadErr(typeof msg === "string" ? msg
          : "Ödeme oturumu başlatılamadı. Lütfen daha sonra tekrar deneyin.");
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => { ok = false; };
  }, [plan, renewalToken]);

  const validate = () => {
    const digits = (cardNumber || "").replace(/\D/g, "");
    if (!ccOwner.trim()) return "Kart üzerindeki isim gereklidir.";
    if (digits.length < 12 || digits.length > 19) return "Kart numarası eksik veya hatalı.";
    if (!expiryMonth || !/^\d{1,2}$/.test(expiryMonth) || Number(expiryMonth) < 1 || Number(expiryMonth) > 12)
      return "Son kullanma ayı hatalı.";
    if (!expiryYear || !/^\d{2}(\d{2})?$/.test(expiryYear))
      return "Son kullanma yılı hatalı.";
    if (!cvv || !/^\d{3,4}$/.test(cvv)) return "CVV eksik.";
    return "";
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setFailMsg("");
    const err = validate();
    if (err) { setFailMsg(err); return; }
    setSubmitting(true);
    // Native HTML form POST — browser navigation.  Kart alanları backend'e
    // GİTMEZ, doğrudan PayTR'a POST edilir.  Kısa timeout sonrası
    // formRef.submit() gerçek submit'i tetikler.
    setTimeout(() => {
      try {
        formRef.current?.submit();
      } catch (subErr) {
        setSubmitting(false);
        setFailMsg("Ödeme formu gönderilemedi. Lütfen sayfayı yenileyip tekrar deneyin.");
      }
    }, 50);
  };

  if (loading) {
    return (
      <SectionCard title="Ödeme Hazırlanıyor..." testId="direct-payment-card">
        <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Güvenli ödeme oturumu oluşturuluyor...
        </div>
      </SectionCard>
    );
  }

  if (loadErr || !session) {
    return (
      <SectionCard title="Ödeme Başlatılamadı" testId="direct-payment-card">
        <div className="p-6 space-y-3">
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3"
               data-testid="direct-payment-load-error">
            {loadErr || "Bilinmeyen hata."}
          </div>
          {onBack && (
            <Button variant="outline" onClick={onBack} data-testid="direct-payment-back">
              Geri
            </Button>
          )}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title={`Ödeme — ${planLabel} Plan (${displayTotal}, KDV Dahil)`}
      testId="direct-payment-card"
    >
      <div className="p-5 space-y-4">
        <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <CreditCard className="w-5 h-5" />
          </div>
          <div className="text-sm">
            <div className="font-medium text-slate-900" data-testid="direct-payment-plan-summary">
              {planLabel} plan — <span className="text-emerald-700">{displayTotal}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {renewalHint}. Kart bilgileriniz doğrudan PayTR güvenli altyapısına iletilir; Penz sunucusuna gönderilmez ve saklanmaz.
            </div>
          </div>
        </div>

        {/* Bilgi: form action DOĞRUDAN PayTR'a POST edilir. */}
        <form
          ref={formRef}
          action={session.action}
          method="POST"
          onSubmit={onSubmit}
          className="space-y-4"
          data-testid="direct-payment-form"
          autoComplete="on"
        >
          {/* Backend'den gelen kart-DIŞI hidden fields */}
          {Object.entries(session.fields || {}).map(([k, v]) => (
            <input key={k} type="hidden" name={k} value={v ?? ""} />
          ))}

          {/* Kart alanları — hidden inputs PayTR'a NORMALIZE edilmiş
              (boşluksuz kart no, 2 haneli yıl) gider.  Görsel input'lar
              yalnızca kullanıcı deneyimi içindir ve PayTR'a submit
              edilmez (name yok). */}
          <input type="hidden" name="cc_owner" value={ccOwner.trim()} />
          <input type="hidden" name="card_number" value={(cardNumber || "").replace(/\D/g, "")} />
          <input type="hidden" name="expiry_month" value={expiryMonth.padStart(2, "0")} />
          <input type="hidden" name="expiry_year"
                 value={expiryYear.length === 4 ? expiryYear.slice(-2) : expiryYear} />
          <input type="hidden" name="cvv" value={cvv} />

          <div className="space-y-1.5">
            <Label className="text-xs">Kart Üzerindeki İsim</Label>
            <Input
              value={ccOwner}
              onChange={(e) => setCcOwner(e.target.value)}
              placeholder="Ad Soyad"
              maxLength={60}
              required
              autoComplete="cc-name"
              className="h-10 rounded-lg"
              data-testid="cc-owner-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Kart Numarası</Label>
            <Input
              inputMode="numeric"
              value={cardMasked}
              onChange={(e) => setCardNumber(e.target.value.replace(/\s/g, ""))}
              placeholder="1234 5678 9012 3456"
              maxLength={23}
              required
              autoComplete="cc-number"
              className="h-10 rounded-lg font-mono tracking-wider"
              data-testid="card-number-input"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Ay</Label>
              <Input
                inputMode="numeric"
                value={expiryMonth}
                onChange={(e) => setExpiryMonth(e.target.value.replace(/\D/g, "").slice(0, 2))}
                placeholder="AA"
                maxLength={2}
                required
                autoComplete="cc-exp-month"
                className="h-10 rounded-lg text-center"
                data-testid="expiry-month-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Yıl</Label>
              <Input
                inputMode="numeric"
                value={expiryYear}
                onChange={(e) => setExpiryYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="YY"
                maxLength={4}
                required
                autoComplete="cc-exp-year"
                className="h-10 rounded-lg text-center"
                data-testid="expiry-year-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">CVV</Label>
              <Input
                inputMode="numeric"
                type="password"
                value={cvv}
                onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="•••"
                maxLength={4}
                required
                autoComplete="cc-csc"
                className="h-10 rounded-lg text-center tracking-wider"
                data-testid="cvv-input"
              />
            </div>
          </div>

          {failMsg && (
            <div
              className="text-xs px-3 py-2 rounded-lg bg-rose-50 text-rose-700 border border-rose-200"
              data-testid="direct-payment-error"
            >
              {failMsg}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            3D Secure ile korumalı ödeme — bankanız SMS/onay isteyebilir.
          </div>

          <div className="flex items-center gap-3">
            {onBack && (
              <Button type="button" variant="outline" onClick={onBack} disabled={submitting}
                      data-testid="direct-payment-back">
                Geri
              </Button>
            )}
            <Button
              type="submit"
              className="flex-1 bg-slate-900 hover:bg-slate-800"
              disabled={submitting}
              data-testid="direct-payment-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> PayTR'a yönlendiriliyor...</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" /> {displayTotal} Öde</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </SectionCard>
  );
}
